/**
 * Finding/vulnerability API methods (list, filter, update status, rescan).
 */

import apiClient from './client';
import type { Finding, FindingFilters, FindingStatusUpdateRequest, FindingSummary } from '@/types/finding';

/** List findings with optional filters. */
export async function getFindings(filters?: FindingFilters): Promise<Finding[]> {
  const { data } = await apiClient.get<Finding[]>('/v1/findings', { params: filters });
  return data;
}

/** Get aggregated finding summary statistics. */
export async function getFindingSummary(): Promise<FindingSummary> {
  const { data } = await apiClient.get<FindingSummary>('/v1/findings/summary');
  return data;
}

/** Get a single finding by ID. */
export async function getFinding(findingId: string): Promise<Finding> {
  const { data } = await apiClient.get<Finding>(`/v1/findings/${findingId}`);
  return data;
}

/** Update a finding's status (e.g., mark as resolved). */
export async function updateFindingStatus(
  findingId: string,
  payload: FindingStatusUpdateRequest,
): Promise<Finding> {
  const { data } = await apiClient.put<Finding>(`/v1/findings/${findingId}/status`, payload);
  return data;
}

/** Trigger a targeted rescan for a specific finding. */
export async function triggerRescan(findingId: string): Promise<{ scan_id: string }> {
  const { data } = await apiClient.post<{ scan_id: string }>(`/v1/findings/${findingId}/rescan`);
  return data;
}

/** Delete a single finding by ID. */
export async function deleteFinding(findingId: string): Promise<void> {
  await apiClient.delete(`/v1/findings/${findingId}`);
}

/** Delete multiple findings by ID. */
export async function bulkDeleteFindings(ids: string[]): Promise<{ deleted: number }> {
  const { data } = await apiClient.post<{ deleted: number }>('/v1/findings/bulk-delete', { ids });
  return data;
}
